import React, { useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import classes from './EventsSheet.module.css';
import { UserContext } from '../../store/user-context';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../config';
import ConfirmDialog from '../UI/ConfirmDialog';

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#2c3030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 5V19M5 12H19" stroke="#2c3030" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M3 6H21M8 6V4H16V6M19 6L18 20H6L5 6" stroke="#808383" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EventsSheet = ({ onClose, onLeave }) => {
  const UserCtx = useContext(UserContext);
  const groups = UserCtx.groups || [];
  const currentId = UserCtx.current;
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSwitch = (id) => {
    // Only updates the active selection (green tick) — navigation happens
    // separately when the user leaves this screen via the back arrow.
    UserCtx.setCurrent(id);
  };

  const handleAdd = () => {
    UserCtx.setCurrent('new');
    onClose();
  };

  const confirmDelete = async () => {
    const group = pendingDelete;
    if (!group) return;
    setDeleting(true);
    if (group.id === currentId) UserCtx.setCurrent('delete');
    try {
      await deleteDoc(doc(db, 'groups', group.id));
    } catch (err) {
      console.log('Error deleting group:', err);
    }
    setDeleting(false);
    setPendingDelete(null);
  };

  return ReactDOM.createPortal(
    <div className={classes.overlay}>
      <div className={classes.page}>

        <div className={classes.header}>
          <button className={classes.backBtn} onClick={onLeave || onClose} aria-label="Go back">
            <BackIcon />
          </button>
          <span className={classes.title}>My events</span>
        </div>

        <div className={classes.content}>
          <div className={classes.list}>
            {groups.map((group) => {
              const isActive = group.id === currentId;
              const name = group.festival?.name || group.name || 'Unnamed event';
              const location = group.festival?.location
                ? `${group.festival.location}${group.festival.country ? ', ' + group.festival.country : ''}`
                : null;

              return (
                <div
                  key={group.id}
                  className={`${classes.eventRow} ${isActive ? classes.eventRowActive : ''}`}
                  onClick={() => !isActive && handleSwitch(group.id)}
                >
                  <div className={classes.eventInfo}>
                    <span className={classes.eventName}>{name}</span>
                    {location && <span className={classes.eventLocation}>{location}</span>}
                  </div>
                  <div className={classes.rowActions}>
                    {isActive && (
                      <span className={classes.activeCheck}>
                        <CheckIcon />
                      </span>
                    )}
                    <button
                      className={classes.deleteBtn}
                      onClick={(e) => { e.stopPropagation(); setPendingDelete(group); }}
                      aria-label={`Remove ${name}`}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <button className={classes.addBtn} onClick={handleAdd}>
            <span className={classes.addBtnIcon}><PlusIcon /></span>
            <span>Add event</span>
          </button>
        </div>

      </div>

      {pendingDelete && (
        <ConfirmDialog
          message={<>Remove <strong>{pendingDelete.festival?.name || pendingDelete.name || 'this event'}</strong> from your events?</>}
          confirmLabel="Remove"
          workingLabel="Removing..."
          working={deleting}
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>,
    document.body
  );
};

export default EventsSheet;
